import { styled } from '@mui/material/styles';
import { Box, Grid, Paper, Typography, LinearProgress, Tooltip, tooltipClasses } from '@mui/material';
import React from 'react';
import { useRouter } from 'next/router';
import * as send from '../../../../../../lib/analytics/send';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { useFetchTabData } from '../../../../../utils/helpers';
import { dataConstants } from '../../../../../constants';

const { STOCK_SECTION_CODES } = dataConstants;

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#174c86',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#174c86',
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 14,
    fontWeight: 500
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
  borderRadius: '7px',
  marginBottom: '0px!important',
  '& .Item-Head': {
    fontSize: '.7rem',
    background: '#f2f2f2',
    borderBottom: '1px solid #eae9ee',
    marginBottom: 0,
    padding: ' 0 8px 0 8px',
    borderRadius: '6px 6px 0 0',
    '& a': {
      textDecoration: 'none',
    },
    '& .option-title': {
      fontWeight: 500,
      color: '#000',
      fontSize: '.875rem',
    }
  },
  '& .Item-Body': {
    padding: theme.spacing(1),
    color: '#13113c',
    gap: '4px'
  }
}));

const ValuationMetrics = ({ ticker, realtimeData, isPremium, token }) => {
  const router = useRouter();

  // Fetch data using the same hook as TickerToolbar
  let { data: latest_metrics, loading } = useFetchTabData(ticker, isPremium ? "latest_metrics_premium" : "latest_metrics", "NEW_SUM_OD", "LATP");
  let { data: fv_cheap_vs_expensive } = useFetchTabData(ticker, STOCK_SECTION_CODES.FVPROFILECHEAPEXP, "NEW_SUM_OD", "GAUG");

  // Process valuation data
  let fv_cheap_vs_expensive_data = fv_cheap_vs_expensive?.settings?.fv_gauge || [];
  fv_cheap_vs_expensive_data = fv_cheap_vs_expensive_data?.map(d => d.replace(/XXXXX/g, ticker)) || [];
  fv_cheap_vs_expensive_data = latest_metrics?.tabData?.filter(d => fv_cheap_vs_expensive_data.includes(d.code)) || [];
  let arrPrice = fv_cheap_vs_expensive_data && fv_cheap_vs_expensive_data.length > 0 ? fv_cheap_vs_expensive_data.map(a => a.value) : null;
  arrPrice = arrPrice && arrPrice.length > 0 ? [...arrPrice, 0] : null;

  // Calculate gauge percent
  const getGaugePercent = (arrPrice) => {
    const { realtime_stock_data } = realtimeData || {};
    const currPrice = realtime_stock_data && realtime_stock_data.length > 0 ? realtime_stock_data[0].price : undefined;
    const sortedArrPrice = arrPrice && arrPrice.length > 0 ? [...arrPrice].sort((a, b) => a - b) : null;
    const gaugeDivisions = 6;
    const arrReduced = sortedArrPrice && sortedArrPrice.length > 0 ? sortedArrPrice.map(a => currPrice - a) : null;
    const arrIndex = arrReduced && arrReduced.length > 0 ? arrReduced.filter(a => a > 0).length : null;
    const basePct = arrIndex ? (arrIndex - 1) * (1 / gaugeDivisions) : undefined;
    const diffLevel = arrIndex && sortedArrPrice
      ? (arrIndex >= sortedArrPrice.length ? sortedArrPrice[arrIndex - 1] - sortedArrPrice[arrIndex - 3] : sortedArrPrice[arrIndex] - sortedArrPrice[arrIndex - 1])
      : null;
    const diffPrice = currPrice && arrIndex && sortedArrPrice && sortedArrPrice.length > 0 ? currPrice - sortedArrPrice[arrIndex - 1] : null;
    const addPct = diffLevel && diffPrice ? (diffPrice / diffLevel) * (1 / gaugeDivisions) : null;
    let finalPct = (basePct !== null && basePct !== undefined) && (addPct !== null && addPct !== undefined) ? basePct + addPct : null;
    finalPct = finalPct !== null ? Math.min(1, finalPct) : null;
    return finalPct ? finalPct.toFixed(2) : null;
  };

  const fv_cheap_vs_expensiveValue = latest_metrics && latest_metrics?.tabData && latest_metrics?.tabData.length > 0 && arrPrice ? getGaugePercent(arrPrice) : null;
  const gaugeDivisions = 6;
  const fv_text = fv_cheap_vs_expensiveValue &&
    fv_cheap_vs_expensiveValue <= 1 / gaugeDivisions ? 'Very cheap'
    :
    fv_cheap_vs_expensiveValue > 1 / gaugeDivisions && fv_cheap_vs_expensiveValue <= 2 / gaugeDivisions ? 'Cheap'
      :
      fv_cheap_vs_expensiveValue > 2 / gaugeDivisions && fv_cheap_vs_expensiveValue <= 4 / gaugeDivisions ? 'Fair'
        :
        fv_cheap_vs_expensiveValue > 4 / gaugeDivisions && fv_cheap_vs_expensiveValue <= 5 / gaugeDivisions ? 'Expensive'
          :
          fv_cheap_vs_expensiveValue > 5 / gaugeDivisions ? 'Very Expensive'
            :
            '';
  const port_size = latest_metrics && latest_metrics?.tabData && latest_metrics?.tabData.length > 0 ? latest_metrics?.tabData?.find(d => d.code === ("XXXXX.PORTSIZE").replace(/XXXXX/g, ticker)) : null;

  // Use the EXACT same logic as TickerToolbar
  const showValuation = ((fv_cheap_vs_expensiveValue && isPremium) || (!isPremium));
  const showPortfolio = ((port_size && port_size?.value && isPremium) || (!isPremium));

  // Show component if either valuation or portfolio should be shown
  if (!showValuation && !showPortfolio) return null;

  const getValuationColor = () => {
    const gaugeDivisions = 6;
    if (fv_cheap_vs_expensiveValue <= 1 / gaugeDivisions) return '#10b981';
    if (fv_cheap_vs_expensiveValue <= 2 / gaugeDivisions) return '#16a34a';
    if (fv_cheap_vs_expensiveValue <= 4 / gaugeDivisions) return '#f59e0b';
    if (fv_cheap_vs_expensiveValue <= 5 / gaugeDivisions) return '#DB3353';
    return '#ef4444';
  };

  const handleClick = (e) => {
    if (isPremium) {
      e.preventDefault();
    } else if (!token) {
      router.push('/auth');
      send.uevent(e, 'auth-valuation-metrics');
    } else {
      router.push('/pricing');
      send.uevent(e, 'fair-value-ad');
    }
  };

  const getDisplayText = () => {
    if (!token) return 'Login to View';
    if (token && !isPremium) return 'Check';
    return null;
  };

  return (
    <Grid size={{ xs: 12, sm: 12 }}>
      <Item
        component="div"
        sx={{
          mb: '8px',
          cursor: isPremium ? 'default' : 'pointer',
        }}
        onClick={handleClick}
      >
        <Box
          className="Item-Head"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 0.5,
            px: 0.5,
          }}
        >
          <Typography component="h3" className="option-title" sx={{ mr: 'auto', fontSize: '.875rem !important' }}>
            {ticker} Stock Valuation Analysis
          </Typography>
        </Box>

        <Box className="Item-Body" sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {showValuation && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <LightTooltip disableInteractive title="Long-term Valuation based on Grufity's Fair Value model." arrow>
                <Typography variant="subtitle2" sx={{ fontSize: '.875rem', fontWeight: 600, color: '#000', cursor: 'help' }}>
                  Valuation
                </Typography>
              </LightTooltip>
              {(isPremium && fv_text) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontSize: '.875rem',
                      fontWeight: 700,
                      color: getValuationColor(),
                      textTransform: 'uppercase'
                    }}
                  >
                    {fv_text}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '.875rem',
                    fontWeight: 600,
                    color: '#174c86',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': !token ? { textDecoration: 'underline' } : {}
                  }}
                >
                  {getDisplayText()}
                  {!token && <KeyboardArrowRightRoundedIcon sx={{ fontSize: '1rem' }} />}
                </Typography>
              )}
            </Box>
          )}

          {showPortfolio && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <LightTooltip disableInteractive title="If our model created a portfolio, then this is the 'maximum' portfolio percentage it will allocate to this stock. This is based on valuation, diversification, risk-reward profile and the quality of the stock. Your view might be very different." arrow>
                <Typography variant="subtitle2" sx={{ fontSize: '.875rem', fontWeight: 600, color: '#000', cursor: 'help' }}>
                  Max Portfolio %
                </Typography>
              </LightTooltip>
              {(isPremium && port_size && port_size.value) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '.875rem', fontWeight: 600, color: '#174c86' }}>
                    {parseFloat(port_size.value).toFixed(2)}%
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '.875rem',
                    fontWeight: 600,
                    color: '#174c86',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': !token ? { textDecoration: 'underline' } : {}
                  }}
                >
                  {getDisplayText()}
                  {!token && <KeyboardArrowRightRoundedIcon sx={{ fontSize: '1rem' }} />}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Item>
    </Grid>
  );
}

export default React.memo(ValuationMetrics);
